import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Brush, CartesianGrid, Label, Line, LineChart, ResponsiveContainer, XAxis, YAxis,
} from 'recharts';

import { Settings } from '@mui/icons-material';
import PlotSettings from '../PlotSettings';
import PlotStats from '../PlotStats';

const rtd1Stroke = '#035320';
const rtd2Stroke = '#ff5733';

const measure = '';
const unit = 'Celsius';

const CHART_HEIGHT = 400;
const CHART_WIDTH = 660;
const COMPACT_HEIGHT = 280;

export default function Thermal({
  data = [],
  minRTD,
  maxRTD,
  hideSettings = false,
  compact = false,
}) {
  const [yMin, setYMin] = useState(minRTD || 10);
  const [yMax, setYMax] = useState(maxRTD || 25);
  const [rtdTickCount, setRtdTickCount] = useState(8);

  const [chartWindowMinNum, setChartWindowMinNum] = useState(0);

  const [start, setStartIndex] = useState(0);
  const [end, setEndIndex] = useState(0);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [ev1Active, setEV1Active] = useState(true);
  const [ev2Active, setEV2Active] = useState(true);

  const closeSettings = () => setSettingsOpen(false);

  const handleBrushUpdate = ({
    startIndex,
    endIndex,
  }) => {
    setStartIndex(startIndex);
    setEndIndex(((data.length - 1) - endIndex <= 1) ? null : endIndex);
  };

  const togglePlotSettings = () => setSettingsOpen(!settingsOpen);

  const last = data.at(-1);
  const hasData = data.length > 0;
  const displayTimestamp = last?.formattedTimestamp
    || (last?.timestamp != null ? new Date(last.timestamp).toLocaleString() : undefined);

  return (
    <div className={`heart-rate plot${compact ? ' compact' : ''}`}>
      <div className="header">
        <span>Thermal RTD</span>
        {!hideSettings && (
        <div className="settings-controller">
          <Settings data-testid="thermal-plot-settings" onClick={togglePlotSettings} />
          {settingsOpen
            ? (
              <PlotSettings
                setYMin={setYMin}
                startYMin={yMin}
                setYMax={setYMax}
                startYMax={yMax}
                setTickCount={setRtdTickCount}
                startTickCount={rtdTickCount}
                setChartWindow={setChartWindowMinNum}
                startChartWindow={chartWindowMinNum}
              />
            ) : null}
        </div>
        )}
      </div>
      <div className="stats flex-row" onClick={closeSettings} role="presentation">
        <PlotStats
          color={rtd1Stroke}
          data={Number(last?.rtd1_temperature).toFixed(2)}
          isActive={ev1Active}
          setActive={() => setEV1Active(!ev1Active)}
          timestamp={displayTimestamp}
          hasData={hasData}
          ev="RTD1"
          unit={unit}
        />
        <PlotStats
          color={rtd2Stroke}
          data={Number(last?.rtd2_temperature).toFixed(2)}
          isActive={ev2Active}
          setActive={() => setEV2Active(!ev2Active)}
          timestamp={displayTimestamp}
          hasData={hasData}
          ev="RTD2"
          unit={unit}
        />
      </div>
      <span>{unit}</span>
      <div className="thermal-chart-container" onClick={closeSettings} role="presentation">
        {compact ? (
          <ResponsiveContainer width="100%" height={COMPACT_HEIGHT}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" interval="preserveStartEnd" />
              <YAxis
                yAxisId={measure}
                domain={[Math.max((yMin == null ? minRTD : yMin), 0), yMax || maxRTD]}
                interval={0}
                tickCount={rtdTickCount}
                tickFormatter={Math.round}
                allowDataOverflow
                type="number"
              >
                <Label position="top" textAnchor="start" value={unit} />
              </YAxis>
              {ev1Active
                ? (
                  <Line
                    animationDuration={50}
                    animationEasing="linear"
                    dataKey="rtd1_temperature"
                    dot={false}
                    isAnimationActive
                    key="rtd1_temperature"
                    stroke={rtd1Stroke}
                    strokeWidth={2}
                    type="monotone"
                    yAxisId={measure}
                  />
                ) : null}
              {ev2Active && data.length > 0
                ? (
                  <Line
                    animationDuration={50}
                    animationEasing="linear"
                    dataKey="rtd2_temperature"
                    dot={false}
                    isAnimationActive
                    key="rtd2_temperature"
                    stroke={rtd2Stroke}
                    strokeWidth={2}
                    type="monotone"
                    yAxisId={measure}
                  />
                ) : null}
              {(data && data.length)
                ? (
                  <Brush
                    data={data}
                    dataKey="name"
                    height={compact ? 24 : 40}
                    padding={{
                      left: 0,
                      right: 10,
                    }}
                    tick
                    startIndex={start || (chartWindowMinNum ? Math.max(data.length - 60 * chartWindowMinNum, 0) : 0)}
                    endIndex={end || (data.length - 1)}
                    onChange={handleBrushUpdate}
                    travellerWidth={6}
                  >
                    <LineChart data={data}>
                      <Line
                        animationDuration={50}
                        animationEasing="linear"
                        dataKey="rtd1_temperature"
                        key="rtd1_temperature"
                        dot={false}
                        isAnimationActive
                        stroke={rtd1Stroke}
                        type="monotone"
                      />
                      <Line
                        animationDuration={50}
                        animationEasing="linear"
                        dataKey="rtd2_temperature"
                        key="rtd2_temperature"
                        dot={false}
                        isAnimationActive
                        stroke={rtd2Stroke}
                        type="monotone"
                      />
                    </LineChart>
                  </Brush>
                ) : null}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <LineChart data={data} height={CHART_HEIGHT} width={CHART_WIDTH}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" interval="preserveStartEnd" />
            <YAxis
              yAxisId={measure}
              domain={[Math.max((yMin == null ? minRTD : yMin), 0), yMax || maxRTD]}
              interval={0}
              tickCount={rtdTickCount}
              tickFormatter={Math.round}
              allowDataOverflow
              type="number"
            >
              <Label position="top" textAnchor="start" value={unit} />
            </YAxis>
            {ev1Active
              ? (
                <Line
                  animationDuration={50}
                  animationEasing="linear"
                  dataKey="rtd1_temperature"
                  dot={false}
                  isAnimationActive
                  key="rtd1_temperature"
                  stroke={rtd1Stroke}
                  strokeWidth={2}
                  type="monotone"
                  yAxisId={measure}
                />
              ) : null}
            {ev2Active && data.length > 0
              ? (
                <Line
                  animationDuration={50}
                  animationEasing="linear"
                  dataKey="rtd2_temperature"
                  dot={false}
                  isAnimationActive
                  key="rtd2_temperature"
                  stroke={rtd2Stroke}
                  strokeWidth={2}
                  type="monotone"
                  yAxisId={measure}
                />
              ) : null}
            {(data && data.length)
              ? (
                <Brush
                  data={data}
                  dataKey="name"
                  height={40}
                  padding={{
                    left: 0,
                    right: 10,
                  }}
                  tick
                  startIndex={start || (chartWindowMinNum ? Math.max(data.length - 60 * chartWindowMinNum, 0) : 0)}
                  endIndex={end || (data.length - 1)}
                  onChange={handleBrushUpdate}
                  travellerWidth={6}
                >
                  <LineChart data={data}>
                    <Line
                      animationDuration={50}
                      animationEasing="linear"
                      dataKey="rtd1_temperature"
                      key="rtd1_temperature"
                      dot={false}
                      isAnimationActive
                      stroke={rtd1Stroke}
                      type="monotone"
                    />
                    <Line
                      animationDuration={50}
                      animationEasing="linear"
                      dataKey="rtd2_temperature"
                      key="rtd2_temperature"
                      dot={false}
                      isAnimationActive
                      stroke={rtd2Stroke}
                      type="monotone"
                    />
                  </LineChart>
                </Brush>
              ) : null}
          </LineChart>
        )}
      </div>
    </div>
  );
}

Thermal.propTypes = {
  data: PropTypes.any,
  minRTD: PropTypes.number,
  maxRTD: PropTypes.number,
  hideSettings: PropTypes.bool,
  compact: PropTypes.bool,
};
